import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowLeft, Star } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { PricingEngine, resolveSharedTiers } from '../../../lib/utils/pricingEngine';
import { CenterEditor } from './CenterEditor';
import { ContextPanel } from './ContextPanel';
import { ProposalTree } from './ProposalTree';
const SOURCE_LABELS = {
    xlsx_extraction: 'XLSX Template',
    pws_inference: 'PWS Inferred',
    manual: 'Manual'
};
export const V2PricingWorkspace = /*#__PURE__*/ memo(function V2PricingWorkspace({ projectId, pricing, scenarioName, onBack, updateMutation }) {
    const [selectedNodeId, setSelectedNodeId] = useState('overview');
    // Local state for editing
    const [localClinPricing, setLocalClinPricing] = useState(pricing.clinPricing ?? []);
    const [localRateCard, setLocalRateCard] = useState(pricing.rateCard);
    const [localIndirectConfig, setLocalIndirectConfig] = useState(pricing.globalIndirectConfig);
    const [localSharedTierConfigs, setLocalSharedTierConfigs] = useState(pricing.sharedTierConfigs ?? []);
    const [isDirty, setIsDirty] = useState(false);
    // When the parent's pricing prop refreshes — typically because an
    // edit elsewhere (the LCAT detail drawer) invalidated the pricing
    // cache and react-query refetched with the new canonical rates — we
    // re-seed local state so the rate card reflects the change without
    // a manual reload.
    //
    // Guarded on !isDirty so we never trample work the user is actively
    // editing. If the user has unsaved changes when an external refresh
    // arrives, their edits win; the next save will reconcile.
    //
    // isDirty is in the dep array because the rules-of-hooks linter
    // requires it. That means the effect also runs when the user starts
    // (true) or finishes (false) editing — both run the guard first, so
    // the only real work happens when isDirty=false AND `pricing` is the
    // value we want to mirror. The duplicate no-op runs on isDirty flips
    // are cheap.
    useEffect(()=>{
        if (isDirty) return;
        setLocalClinPricing(pricing.clinPricing ?? []);
        setLocalRateCard(pricing.rateCard);
        setLocalIndirectConfig(pricing.globalIndirectConfig);
        setLocalSharedTierConfigs(pricing.sharedTierConfigs ?? []);
    }, [
        pricing,
        isDirty
    ]);
    const engine = useMemo(()=>new PricingEngine(), []);
    // Recompute all CLIN totals — resolve shared tiers before evaluation
    const recomputedClinPricing = useMemo(()=>{
        if (!localRateCard) return localClinPricing;
        const globals = {
            feeMethod: pricing.globalFeeMethod,
            indirectConfig: localIndirectConfig
        };
        return localClinPricing.map((clin)=>{
            const resolved = clin.laborStructure.type === 'tiered_shared' && localSharedTierConfigs.length > 0 ? {
                ...clin,
                laborStructure: resolveSharedTiers(clin.laborStructure, localSharedTierConfigs)
            } : clin;
            return engine.evaluateCLIN(resolved, localRateCard, globals);
        });
    }, [
        localClinPricing,
        localRateCard,
        localIndirectConfig,
        localSharedTierConfigs,
        pricing.globalFeeMethod,
        engine
    ]);
    const handleCLINChange = useCallback((updated)=>{
        setLocalClinPricing((prev)=>prev.map((c)=>c.clinNumber === updated.clinNumber ? updated : c));
        setIsDirty(true);
    }, []);
    const handleRateCardChange = useCallback((updated)=>{
        setLocalRateCard(updated);
        setIsDirty(true);
    }, []);
    const handleIndirectConfigChange = useCallback((updated)=>{
        setLocalIndirectConfig(updated);
        setIsDirty(true);
    }, []);
    const handleSharedTierChange = useCallback((updated)=>{
        setLocalSharedTierConfigs((prev)=>prev.map((c)=>c.id === updated.id ? updated : c));
        setIsDirty(true);
    }, []);
    const handleSave = useCallback(()=>{
        updateMutation.mutate({
            clinPricing: localClinPricing,
            rateCard: localRateCard,
            sharedTierConfigs: localSharedTierConfigs.length > 0 ? localSharedTierConfigs : undefined,
            globalIndirectConfig: localIndirectConfig
        });
        setIsDirty(false);
    }, [
        localClinPricing,
        localRateCard,
        localSharedTierConfigs,
        localIndirectConfig,
        updateMutation
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "h-full flex flex-col",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                onClick: onBack,
                                className: "flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700",
                                children: [
                                    /*#__PURE__*/ _jsx(ArrowLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Back"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-lg font-semibold",
                                        children: scenarioName
                                    }),
                                    pricing.isPrimary && /*#__PURE__*/ _jsxs(Badge, {
                                        variant: "secondary",
                                        className: "bg-amber-100 text-amber-800 border-amber-200 text-xs px-2 py-0.5",
                                        children: [
                                            /*#__PURE__*/ _jsx(Star, {
                                                className: "w-3.5 h-3.5 mr-0.5 inline"
                                            }),
                                            "Primary"
                                        ]
                                    }),
                                    pricing.contractType && /*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs",
                                        children: pricing.contractType
                                    }),
                                    pricing.templateSource && /*#__PURE__*/ _jsx(Badge, {
                                        variant: "outline",
                                        className: "text-xs bg-green-50 text-green-700 border-green-200",
                                        children: SOURCE_LABELS[pricing.templateSource] || pricing.templateSource
                                    })
                                ]
                            })
                        ]
                    }),
                    isDirty && /*#__PURE__*/ _jsx("button", {
                        "data-testid": Testids.pricingRateCard.saveChanges,
                        onClick: handleSave,
                        disabled: updateMutation.isPending,
                        className: "px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50",
                        children: updateMutation.isPending ? 'Saving...' : 'Save Changes'
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 flex min-h-0",
                children: [
                    /*#__PURE__*/ _jsx("aside", {
                        className: "w-60 border-r border-gray-200 overflow-y-auto flex-shrink-0 bg-gray-50/50",
                        children: /*#__PURE__*/ _jsx(ProposalTree, {
                            pricing: pricing,
                            clinPricing: recomputedClinPricing,
                            sharedTierConfigs: localSharedTierConfigs,
                            selectedNodeId: selectedNodeId,
                            onSelect: setSelectedNodeId
                        })
                    }),
                    /*#__PURE__*/ _jsx("main", {
                        className: "flex-1 overflow-y-auto px-6 py-4",
                        children: /*#__PURE__*/ _jsx(CenterEditor, {
                            projectId: projectId,
                            selectedNodeId: selectedNodeId,
                            pricing: pricing,
                            clinPricing: recomputedClinPricing,
                            rateCard: localRateCard,
                            sharedTierConfigs: localSharedTierConfigs,
                            indirectConfig: localIndirectConfig,
                            onCLINChange: handleCLINChange,
                            onRateCardChange: handleRateCardChange,
                            onIndirectConfigChange: handleIndirectConfigChange,
                            onSharedTierChange: handleSharedTierChange,
                            onSelectNode: setSelectedNodeId
                        })
                    }),
                    /*#__PURE__*/ _jsx("aside", {
                        className: "w-72 border-l border-gray-200 overflow-y-auto flex-shrink-0 p-4",
                        children: /*#__PURE__*/ _jsx(ContextPanel, {
                            selectedNodeId: selectedNodeId,
                            pricing: pricing,
                            clinPricing: recomputedClinPricing,
                            sharedTierConfigs: localSharedTierConfigs
                        })
                    })
                ]
            })
        ]
    });
});
